import { Injectable } from '@angular/core'

import { PageInfo } from '../../common'
import { ADMIN_API_ENDPOINTS } from '../../common/api-endpoints'
import {
  FindStampRequestDto,
  PageResultDto,
  RejectStampRequestDto,
  StampRequestDto,
  StampRequestFileDto,
  StampRequestMilestoneOptionDto,
  localizedToTranslation
} from '../../dto'
import { FileService } from '../file.service'
import { HttpService } from '../http.service'

import { AdminChallengeMilestoneIndexService } from './admin-challenge-milestone-index.service'

/**
 * FR-05 — Quản lý Stamp Request.
 *
 * ✅ ĐÃ LIVE toàn bộ (OpenAPI 2026-08-25) — mock đã gỡ.
 *   #17 GET  /stamp-collection/challenge-stamp-requests            → list()
 *   #18 GET  /stamp-collection/challenge-stamp-requests/:id        → getById()
 *   #19 POST /stamp-collection/challenge-stamp-requests/:id/approve → approve()
 *   #20 POST /stamp-collection/challenge-stamp-requests/:id/reject  → reject()
 *
 * ⚠️ Schema thật khác `API Design.md` ở 5 điểm — xem chú thích từng hàm.
 */
@Injectable({
  providedIn: 'root'
})
export class AdminStampRequestService {

  constructor(
    private httpService: HttpService,
    private fileService: FileService,
    private milestoneIndexService: AdminChallengeMilestoneIndexService
  ) {}

  /**
   * API #17 — FR-05-BR-01.
   *
   * ⚠️ Phân trang là **`page` + `limit`** (OpenAPI), KHÔNG phải `limit` + `offset` như
   * các API khác trong cùng nhóm stamp-collection.
   *
   * Tab Stamp Request nằm trong Challenge Detail ⇒ gửi `challengeId` để BE lọc theo
   * challenge đang xem, `count` khớp đúng với phân trang.
   *
   * ✅ OpenAPI có thêm filter `mallId` (không có trong `API Design.md`).
   */
  public async list(
    params: FindStampRequestDto
  ): Promise<{ rows: StampRequestDto[]; count: number; pageInfo: PageInfo }> {
    const page = params.page || 1
    const pageSize = params.pageSize || 10

    const queryParams: any = { page, limit: pageSize }

    if (params.challengeId) queryParams.challengeId = params.challengeId
    if (params.milestoneId) queryParams.milestoneId = params.milestoneId
    if (params.milestoneName) queryParams.milestoneName = params.milestoneName
    if (params.mallId) queryParams.mallId = params.mallId
    if (params.status !== null && params.status !== undefined) queryParams.status = params.status
    if (params.fromDate) queryParams.from = params.fromDate
    if (params.toDate) queryParams.to = params.toDate
    if (params.search) queryParams.search = params.search

    const result = await this.httpService.get<PageResultDto<any>>(
      ADMIN_API_ENDPOINTS.CHALLENGE_STAMP_REQUEST,
      queryParams
    )

    const milestoneIndex = await this.milestoneIndexService.get(params.challengeId)
    const rows = (result?.rows || []).map(row => this.toStampRequest(row, milestoneIndex))

    const pageInfo = PageInfo.create()
    pageInfo.page = page
    pageInfo.pageSize = pageSize
    pageInfo.collectionSize = result?.count || 0

    return { rows, count: result?.count || 0, pageInfo }
  }

  /**
   * API #18 — chi tiết kèm ảnh bằng chứng.
   *
   * ⚠️ Response thật trả **`evidence: [{id, fileId, sortOrder, mimeType}]`** — không phải
   * `files: [{fileId, fileName, url}]` như `API Design.md`. **Không có `url` và `fileName`**
   * ⇒ phải resolve qua `FileService.findMultiple()` (1 request batch cho cả bộ ảnh),
   * nếu không màn detail sẽ hiện toàn ảnh placeholder.
   */
  public async getById(id: string, challengeId?: string): Promise<StampRequestDto> {
    const raw = await this.httpService.get<any>(
      `${ADMIN_API_ENDPOINTS.CHALLENGE_STAMP_REQUEST}/${id}`
    )
    const milestoneIndex = await this.milestoneIndexService.get(challengeId || raw?.challengeId)
    const request = this.toStampRequest(raw, milestoneIndex)

    request.files = await this.resolveEvidenceUrls(raw?.evidence || [])

    return request
  }

  /**
   * Option Milestone để lọc theo Challenge đang xem.
   *
   * BE không có API `milestone-options` riêng — và không cần: API #10 (Challenge Detail)
   * đã trả `milestones[{id, position, translations}]`, dựng option trực tiếp từ đó.
   */
  public async listMilestoneOptions(challengeId: string): Promise<StampRequestMilestoneOptionDto[]> {
    const milestoneIndex = await this.milestoneIndexService.get(challengeId)

    return Array.from(milestoneIndex.values())
      .sort((a, b) => (a.position || 0) - (b.position || 0))
      .map(milestone =>
        StampRequestMilestoneOptionDto.create({
          id: milestone.id,
          position: milestone.position,
          name: milestone.name
        })
      )
  }

  /**
   * API #19 — FR-05-BR-03: Approve, không cần Reason, BE tự phát 1 tem.
   *
   * ⚠️ Method **POST**; BE có thể trả 200 hoặc 201. Response chỉ có
   * `{id, status, decidedAt, decidedById, reason, stampId}` — hai call site hiện đều
   * refetch sau khi xử lý nên không cần merge.
   * ⚠️ `409` = request đã bị người khác xử lý (race) — component nên refetch để hiện
   * trạng thái mới thay vì báo lỗi chung chung.
   */
  public async approve(id: string, currentUserName?: string): Promise<StampRequestDto> {
    const raw = await this.httpService.post<any>(
      `${ADMIN_API_ENDPOINTS.CHALLENGE_STAMP_REQUEST}/${id}/approve`
    )

    return this.toDecisionResult(raw, currentUserName)
  }

  /**
   * API #20 — FR-05-BR-04: Reject, Reason bắt buộc, không phát tem.
   * ⚠️ Response **không có `stampId`** (khác approve).
   */
  public async reject(
    id: string,
    data: RejectStampRequestDto,
    currentUserName?: string
  ): Promise<StampRequestDto> {
    const raw = await this.httpService.post<any>(
      `${ADMIN_API_ENDPOINTS.CHALLENGE_STAMP_REQUEST}/${id}/reject`,
      data
    )

    return this.toDecisionResult(raw, currentUserName)
  }

  // ===============================================================================================
  // Mapping BE → FE
  // ===============================================================================================

  private toStampRequest(
    raw: any,
    milestoneIndex: Map<string, { position?: number }>
  ): StampRequestDto {
    const milestoneId = raw?.milestoneId

    return StampRequestDto.create({
      id: raw?.id,
      challengeId: raw?.challengeId,
      challengeName: raw?.challengeName,
      // ⚠️ OpenAPI **không có** `challengeCustomerId` (khác API Design.md) — chỉ có
      // `customerId`. N1 coi như đã chốt: dùng `customerId`.
      customerId: raw?.customerId,
      customerName: raw?.customerName,
      phoneNumber: raw?.customerPhoneNumber,
      challengeMilestoneId: milestoneId,
      milestoneName: localizedToTranslation(raw?.milestoneName),
      // ⚠️ BE-MISSING: `milestonePosition` (mã MS-00x) — join từ API #10 khi API đó lên
      milestonePosition: milestoneIndex.get(milestoneId)?.position,
      // ✅ OpenAPI trả `mallId` + `mallName` (API Design.md không có)
      mallId: raw?.mallId,
      mallName: raw?.mallName,
      attemptNo: raw?.attemptNo,
      status: raw?.status,
      submittedAt: raw?.submittedAt,
      decidedAt: raw?.decidedAt,
      // ⚠️ BE trả tên `decidedById` (không phải `decidedBy`)
      decidedBy: raw?.decidedById,
      decidedByName: raw?.decidedByName,
      reason: raw?.reason,
      stampId: raw?.stampId,
      isRewardOutOfStock: raw?.isRewardOutOfStock
      // ⚠️ BE-MISSING N5: `requestCode` (#AT26062F6S84) không có ở cả #17 và #18.
    })
  }

  /**
   * `evidence[{id, fileId, sortOrder, mimeType}]` → `files[]` kèm `url` + `name`.
   * BE không trả URL nên gom toàn bộ `fileId` thành **1 request** `/v1/files/multiple`.
   */
  private async resolveEvidenceUrls(evidence: any[]): Promise<StampRequestFileDto[]> {
    const items = (evidence || [])
      .slice()
      .sort((a, b) => (a?.sortOrder || 0) - (b?.sortOrder || 0))

    if (!items.length) {
      return []
    }

    const urlById = new Map<string, { url?: string; name?: string }>()

    try {
      const files = await this.fileService.findMultiple(items.map(item => item.fileId))
      ;(files || []).forEach(file => urlById.set(file.id, { url: file.url, name: file.name }))
    } catch (error) {
      // Không chặn màn detail nếu file service lỗi — ảnh rơi về placeholder
    }

    return items.map((item, index) =>
      StampRequestFileDto.create({
        id: item?.id,
        fileId: item?.fileId,
        name: urlById.get(item?.fileId)?.name || `evidence-${index + 1}`,
        url: urlById.get(item?.fileId)?.url,
        sortOrder: item?.sortOrder ?? index + 1,
        mimeType: item?.mimeType
      })
    )
  }

  /** Response rút gọn của API #19/#20. */
  private toDecisionResult(raw: any, currentUserName?: string): StampRequestDto {
    return StampRequestDto.create({
      id: raw?.id,
      status: raw?.status,
      decidedAt: raw?.decidedAt,
      decidedBy: raw?.decidedById,
      // BE không trả tên người xử lý ở #19/#20 → điền lạc quan từ user hiện tại
      decidedByName: currentUserName,
      reason: raw?.reason,
      stampId: raw?.stampId
    })
  }
}
